import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  TextField,
  useTheme,
  Link,
  Stack,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { useAuth } from 'auth'
import logger from 'logger'

import InputLabel from 'components/atoms/InputLabel'

const MfaVerify = ({
  setView,
  state,
  setError,
  error,
  setState,
  setFetching,
  fetching,
  setLoginError,
}) => {
  const theme = useTheme()
  const { AmplifyAuth, authEvent, updateAuth } = useAuth()
  const { t } = useTranslation('auth')

  const inputEl = useRef(null)

  useEffect(() => {
    setState((s) => ({ ...s, otpCode: '' }))
  }, [setState])

  useEffect(() => {
    setTimeout(() => {
      if (inputEl?.current) {
        inputEl.current.focus()
      }
    }, 50)
  }, [inputEl])

  useEffect(() => {
    setError(null)
  }, [setError])

  const handleInputChange = (event) => {
    setLoginError()
    setState((s) => ({ ...s, [event.target.name]: event.target.value }))
  }

  useEffect(() => {
    setFetching(false)
    setState((s) => ({ ...s, otpCode: '' }))
  }, [setFetching, setState])

  const handleVerifyOtp = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    try {
      setFetching(true)

      await AmplifyAuth.confirmSignIn(
        authEvent, // Return object from Auth.signIn()
        state.otpCode, // Confirmation code
        'SOFTWARE_TOKEN_MFA'
      )

      await updateAuth()
      setView('loggedIn')
    } catch (e) {
      setError(e.code)
      logger.error(e)
    } finally {
      setFetching(false)
    }
    return false
  }

  return (
    <Stack
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        width: 0.5,
        maxWidth: 400,
      }}
    >
      <form onSubmit={handleVerifyOtp} style={{ width: '100%' }}>
        <Typography variant="h4" sx={{ pb: 1 }}>
          <strong>{t('headers.mfaVerify.title')}</strong>
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ pb: 5 }}>
          <strong>{t('headers.mfaVerify.subtitle')}</strong>
        </Typography>

        <Box sx={{ width: 1 }}>
          <InputLabel
            label={t('attributes.otpCode.label')}
            inputId="mfa-setup-code"
            sx={{ textAlign: 'left' }}
          />
          <TextField
            id="mfa-setup-code"
            size="small"
            fullWidth
            placeholder={t('attributes.otpCode.placeholder')}
            name="otpCode"
            value={state.otpCode}
            onChange={handleInputChange}
            disabled={fetching}
            autoFocus
            inputProps={{
              ref: inputEl,
            }}
            InputProps={{
              'data-testid': 'mfaVerify-otpCode',
            }}
          />
        </Box>
        <Box mb={3} />
        <LoadingButton
          fullWidth
          width="240px"
          variant="contained"
          color="primary"
          loading={fetching}
          disabled={fetching}
          type="submit"
          data-testid="mfaVerify-button"
        >
          {t('buttons.verifyMfa')}
        </LoadingButton>
        {error && (
          <Typography
            textAlign="center"
            p={[3]}
            style={{ color: theme.palette.error.light }}
            data-testid="mfaVerify-error"
          >
            {t(`errors.${error}`, state)}
          </Typography>
        )}

        <Box mb={3} />
        <Link>
          <Typography
            variant="body2"
            color="primary"
            onClick={() => {
              setState((s) => ({ ...s, password: '', email: '' }))
              setView('signIn')
            }}
            sx={{ cursor: 'pointer' }}
            data-testid="mfaVerify-backToSignin"
          >
            <strong>{t('buttons.loginWithAnotherUser')}</strong>
          </Typography>
        </Link>
      </form>
    </Stack>
  )
}

export default MfaVerify
